/**
 * Work instruction timings of an item, as GET items/timings/{itemKey} returns them. The item modal, the
 * choreography page and Digital Factory all show the same numbers, so the API calculates them once and the
 * clients only lay them out.
 */

export enum EItemTimingStatus {
  completed = 'completed',
  inProgress = 'inProgress',
  notStarted = 'notStarted',
  paused = 'paused',
}

export enum EItemTimingRunState {
  completed = 'completed',
  notStarted = 'notStarted',
  paused = 'paused',
  running = 'running',
}

export interface IItemTimingStep {
  stepId: number;
  /** Started steps only. */
  guideSubmissionId: number | null;
  name: string;
  order: number;
  status: EItemTimingStatus;
  /** Seconds; null when the step has no target. */
  targetDuration: number | null;
  /** Seconds from t0. */
  plannedStart: number;
  /** Seconds from t0. */
  plannedEnd: number;
  /** Seconds worked on the step until calculatedAt. */
  elapsed: number;
  isRunning: boolean;
  isLate: boolean;
  isStartDue: boolean;
  /** 0–100, elapsed against the target. */
  progressPercent: number;
}

export interface IItemTimingWorkInstruction {
  itemGuideAssignmentId: number;
  guideId: number;
  name: string;
  status: EItemTimingStatus;
  isLate: boolean;
  startedAt: string | null;
  endedAt: string | null;
  /** Seconds worked on the steps until calculatedAt (sum of their elapsed, pauses excluded). */
  elapsed: number;
  /** Seconds; the cycle time of the work instruction, or the sum of its step targets. */
  target: number;
  /** Seconds from t0. */
  plannedStart: number;
  /** Seconds from t0. */
  plannedEnd: number;
  completedStepCount: number;
  totalStepCount: number;
  steps: IItemTimingStep[];
}

export interface IItemTiming {
  itemId: number;
  itemKey: string;
  boardId: number;
  /** The moment the response was calculated; running timers keep ticking from here. */
  calculatedAt: string;
  /** The moment the first work instruction of the item was started; null while nothing has started. */
  t0: string | null;
  /** Item timer: first start (the item's first step); null while nothing has started. */
  startedAt: string | null;
  /** Item timer: every work instruction of the item completed. */
  endedAt: string | null;
  /** State of the item timer (paused/resumed from the choreography page, started by any step). */
  state: EItemTimingRunState;
  /** The item timer runs. */
  isRunning: boolean;
  isOverPlan: boolean;
  /** Seconds the item timer ran until calculatedAt, pauses excluded. */
  elapsed: number;
  /** Seconds planned from the first step to the last one. */
  plan: number;
  plannedFinish: string | null;
  completedWorkInstructionCount: number;
  totalWorkInstructionCount: number;
  completedStepCount: number;
  totalStepCount: number;
  workInstructions: IItemTimingWorkInstruction[];
}
